<script>
    import { mapActions, mapMutations, mapState } from 'vuex';
    import { Form, FormItem, Input, Switch, Tooltip } from "element-ui";
    import Vue from "vue";

    Vue.use(Input);
    Vue.use(Form);
    Vue.use(FormItem);
    Vue.use(Switch);
    Vue.use(Tooltip);

    export default {
        name: "password-settings-form",

        mounted() {
            this.getPasswordSettings();
        },

        data() {
            return {
                toolTip: {
                    resetText: 'Number of days a users password is valid before it needs to be reset',
                    reusePolicyText: 'How many of the user\'s previous passwords can\'t be used.',
                    lockoutText: 'The number of times a user can attempt to login with an incorrect password before they\'re locked out of their account. Must be a number between 1 and 10.',
                    symbolText: 'All symbols are allowed - there are no restrictions.',
                    resetTokenText: 'Number of minutes after which password reset link expires if not used',
                },
                switchValues: {
                    on: 1,
                    off: 0
                }
            }
        },

        computed: {
            ...mapState('passwordSettings', [
                'settingsForm',
                'settingsRules'
            ]),
        },

        methods: {
            ...mapActions('passwordSettings', [
                'getPasswordSettings',
            ]),

            ...mapMutations('passwordSettings', [
                'updateValid'
            ]),

            validateForm() {
                if (this.$refs.passwordSettingsForm) {
                    this.$refs.passwordSettingsForm.validate((valid) => {
                        this.updateValid(valid);
                    });
                }
            },
        }
    }
</script>

<template>
    <el-form
        ref="passwordSettingsForm"
        :model="settingsForm"
        :rules="settingsRules"
        @submit.native.prevent>
        <el-form-item label="Minimum character limit" prop="minimum_length">
            <div class="ps-form__label">
                <el-input v-model="settingsForm.minimum_length" min="8" type="number"></el-input>
            </div>
        </el-form-item>

        <el-form-item label="Maximum character limit" prop="maximum_length">
            <div class="ps-form__label">
                <el-input v-model="settingsForm.maximum_length" min="8" type="number"></el-input>
            </div>
        </el-form-item>

        <h5 class="el-form-item__label">Additional Requirements:</h5>
        <el-form-item prop="require_uppercase">
            <div class="ps-form__toggle-label">
                One or more uppercase letters
            </div>

            <div class="ps-form__action">
                <el-switch
                    v-model="settingsForm.require_uppercase"
                    on-text=""
                    off-text=""
                    :on-value="switchValues.on"
                    :off-value="switchValues.off"
                    :width="60">
                </el-switch>
            </div>
        </el-form-item>

        <el-form-item prop="require_lowercase">
            <div class="ps-form__toggle-label">
                One or more lowercase letters
            </div>

            <div class="ps-form__action">
                <el-switch
                    v-model="settingsForm.require_lowercase"
                    on-text=""
                    off-text=""
                    :on-value="switchValues.on"
                    :off-value="switchValues.off"
                    :width="60">
                </el-switch>
            </div>
        </el-form-item>

        <el-form-item prop="require_number">
            <div class="ps-form__toggle-label">
                One or more numbers
            </div>

            <div class="ps-form__action">
                <el-switch
                    v-model="settingsForm.require_number"
                    on-text=""
                    off-text=""
                    :on-value="switchValues.on"
                    :off-value="switchValues.off"
                    :width="60">
                </el-switch>
            </div>
        </el-form-item>

        <el-form-item prop="require_symbol">
            <div class="ps-form__toggle-label">
                One or more symbols
                <el-tooltip
                    class="item text-black"
                    effect="dark"
                    placement="top">
                    <template slot="content">
                        {{ toolTip.symbolText }}
                    </template>
                    <i class="fa fa-question-circle m-l-3"></i>
                </el-tooltip>
            </div>

            <div class="ps-form__action">
                <el-switch
                    v-model="settingsForm.require_symbol"
                    on-text=""
                    off-text=""
                    :on-value="switchValues.on"
                    :off-value="switchValues.off"
                    :width="60">
                </el-switch>
            </div>
        </el-form-item>

        <hr class="ps-form__hr">

        <el-form-item prop="reuse_policy">
            <template slot="label">
                Re-use limit
                <el-tooltip
                    class="item"
                    effect="dark"
                    placement="right">
                    <template slot="content">
                        {{ toolTip.reusePolicyText }}
                    </template>
                    <i class="fa fa-question-circle m-l-3"></i>
                </el-tooltip>
            </template>

            <div class="ps-form__label">
                <el-input
                    v-model="settingsForm.reuse_policy"
                    type="number"
                    min="1"
                    max="30">
                </el-input>
            </div>
        </el-form-item>

        <el-form-item prop="life_in_days">
            <template slot="label">
                Reset period (days)
                <el-tooltip
                    class="item"
                    effect="dark"
                    placement="right">
                    <template slot="content">
                        {{ toolTip.resetText }}
                    </template>
                    <i class="fa fa-question-circle m-l-3"></i>
                </el-tooltip>
            </template>

            <div class="ps-form__label">
                <el-input
                    v-model="settingsForm.life_in_days"
                    type="number"
                    min="30"
                    max="180">
                </el-input>
            </div>
        </el-form-item>

        <el-form-item prop="reset_token_expiration">
            <template slot="label">
                Reset link expiration (minutes)
                <el-tooltip
                    class="item"
                    effect="dark"
                    placement="right">
                    <template slot="content">
                        {{ toolTip.resetTokenText }}
                    </template>
                    <i class="fa fa-question-circle m-l-3"></i>
                </el-tooltip>
            </template>

            <div class="ps-form__label">
                <el-input
                    v-model="settingsForm.reset_token_expiration"
                    type="number"
                    min="15"
                    max="60">
                </el-input>
            </div>
        </el-form-item>

        <el-form-item prop="block_direct_login">
            <div class="ps-form__toggle-label">
                Disable native CRM login
            </div>

            <div class="ps-form__action">
                <el-switch
                    v-model="settingsForm.block_direct_login"
                    on-text=""
                    off-text=""
                    :on-value="switchValues.on"
                    :off-value="switchValues.off"
                    :width="60">
                </el-switch>
            </div>
        </el-form-item>

        <el-form-item prop="lockout_count">
            <div class="ps-form__toggle-label ">
                <label class="el-form-item__label" for="lockout_count">
                    Lockout Count
                </label>
                <el-tooltip
                    class="item text-black"
                    effect="dark"
                    placement="right">
                    <template slot="content">
                        {{ toolTip.lockoutText }}
                    </template>
                    <i class="fa fa-question-circle m-l-3"></i>
                </el-tooltip>
            </div>
            <div class="ps-form__label">
                <el-input
                    name="lockout_count"
                    v-model="settingsForm.lockout_count"
                    type="number"
                    min="1"
                    autofocus>
                </el-input>
            </div>
        </el-form-item>
    </el-form>
</template>
